import { DatePicker, Field, SegmentedControl, SelectInput } from '@bcecloud/portal-ui';
import { STATS_INTERVAL, type StatsInterval } from '@/lib/leads';
import { INTERVAL_LABELS } from '@/lib/report-window';
import {
  ACCOUNT_START,
  PICK_MODE,
  PICK_MODES,
  PICK_MODE_LABELS,
  PPC_PRESETS,
  PPC_PRESET_KEYS,
  addDays,
  monthBounds,
  pickWindow,
  recentMonths,
  windowLabel,
  type PickMode,
  type PpcPreset,
  type PpcSelection,
  type PpcWindow,
} from '@/lib/ppc-window';

/**
 * The PPC page's range and interval controls: the presets in one control,
 * "Pick dates" revealing a from/to range or a single day, week or month,
 * and Daily / Weekly / Monthly beside them. Presentational: the page owns
 * the URL and passes the selection in.
 */

/** The segmented value standing for every picked window. */
const PICK = 'pick';
type RangeOption = PpcPreset | typeof PICK;

/** How many months the month picker lists. */
const MONTH_CHOICES = 36;

const INTERVAL_KEYS: readonly StatsInterval[] = [STATS_INTERVAL.day, STATS_INTERVAL.week, STATS_INTERVAL.month];
const INTERVAL_DISABLED_REASON: Readonly<Record<StatsInterval, string>> = {
  day: 'Daily covers at most 400 days',
  week: 'Weekly needs at least two weeks',
  month: 'Monthly needs at least two months',
};

const monthFormat = new Intl.DateTimeFormat('en-US', { month: 'long', year: 'numeric', timeZone: 'UTC' });
const monthLabel = (month: string): string => monthFormat.format(new Date(`${month}-01T00:00:00Z`));

export interface PpcRangeControlProps {
  readonly selection: PpcSelection;
  readonly span: PpcWindow;
  /** Today on Eastern time: the latest day a picker offers. */
  readonly today: string;
  readonly onPreset: (preset: PpcPreset) => void;
  /** A picked window, both days inclusive. */
  readonly onPick: (mode: PickMode, from: string, to: string) => void;
  readonly onInterval: (interval: StatsInterval) => void;
}

export function PpcRangeControl({ selection, span, today, onPreset, onPick, onInterval }: PpcRangeControlProps): React.ReactElement {
  const lastDay = addDays(span.to, -1);
  const current = { from: span.from, to: lastDay };
  const rangeValue: RangeOption = selection.kind === 'preset' ? selection.preset : PICK;

  function chooseRange(next: RangeOption): void {
    if (next === PICK) onPick(PICK_MODE.custom, current.from, current.to);
    else onPreset(next);
  }

  function chooseMode(mode: PickMode): void {
    const picked = pickWindow(mode, lastDay, current);
    onPick(mode, picked.from, picked.to);
  }

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <SegmentedControl<RangeOption>
          label="Range"
          value={rangeValue}
          onChange={chooseRange}
          options={[
            ...PPC_PRESET_KEYS.map((key) => ({ key, label: PPC_PRESETS[key].label, shortLabel: PPC_PRESETS[key].shortLabel })),
            { key: PICK, label: 'Pick dates', shortLabel: 'Pick' },
          ]}
        />
        <SegmentedControl<StatsInterval>
          label="Interval"
          value={span.interval}
          onChange={onInterval}
          options={INTERVAL_KEYS.map((key) => ({
            key,
            label: INTERVAL_LABELS[key],
            disabled: !span.intervals.has(key),
            disabledReason: INTERVAL_DISABLED_REASON[key],
          }))}
        />
      </div>

      {selection.kind === 'pick' ? (
        <div className="flex flex-wrap items-end gap-4 rounded-portal-card border border-portal-line bg-white p-3">
          <div>
            <span className="mb-1.5 block text-portal-tiny font-bold">Show</span>
            <SegmentedControl<PickMode>
              label="What to pick"
              value={selection.mode}
              onChange={chooseMode}
              options={PICK_MODES.map((key) => ({ key, label: PICK_MODE_LABELS[key] }))}
            />
          </div>
          <Pickers selection={selection} today={today} onPick={onPick} />
        </div>
      ) : null}
    </div>
  );
}

function Pickers({
  selection,
  today,
  onPick,
}: {
  readonly selection: Extract<PpcSelection, { kind: 'pick' }>;
  readonly today: string;
  readonly onPick: PpcRangeControlProps['onPick'];
}): React.ReactElement {
  const { mode, from, to } = selection;
  switch (mode) {
    case PICK_MODE.custom:
      return (
        <>
          <Field label="From" htmlFor="ppc-from" className="w-44">
            <DatePicker
              id="ppc-from"
              value={from}
              min={ACCOUNT_START}
              max={today}
              placeholder="Start"
              onChange={(value) => value !== '' && onPick(mode, value, value > to ? value : to)}
            />
          </Field>
          <Field label="To" htmlFor="ppc-to" className="w-44">
            <DatePicker
              id="ppc-to"
              value={to}
              min={from}
              max={today}
              placeholder="End"
              onChange={(value) => value !== '' && onPick(mode, value < from ? value : from, value)}
            />
          </Field>
        </>
      );
    case PICK_MODE.day:
      return (
        <Field label="Day" htmlFor="ppc-day" className="w-44">
          <DatePicker id="ppc-day" value={from} min={ACCOUNT_START} max={today} onChange={(value) => value !== '' && onPick(mode, value, value)} />
        </Field>
      );
    case PICK_MODE.week:
      return (
        <Field label="Any day in the week" htmlFor="ppc-week" hint={`Monday to Sunday: ${windowLabel({ from, to: addDays(from, 7) })}`} className="w-56">
          <DatePicker
            id="ppc-week"
            value={from}
            min={ACCOUNT_START}
            max={today}
            onChange={(value) => {
              if (value === '') return;
              const week = pickWindow(mode, value);
              onPick(mode, week.from, week.to);
            }}
          />
        </Field>
      );
    case PICK_MODE.month: {
      const months = recentMonths(today, MONTH_CHOICES);
      const chosen = from.slice(0, 7);
      return (
        <Field label="Month" htmlFor="ppc-month" className="w-56">
          <SelectInput
            id="ppc-month"
            value={chosen}
            onChange={(event: React.ChangeEvent<HTMLSelectElement>) => {
              const month = monthBounds(event.target.value);
              onPick(mode, month.from, month.to);
            }}
          >
            {(months.includes(chosen) ? months : [chosen, ...months]).map((month) => (
              <option key={month} value={month}>
                {monthLabel(month)}
              </option>
            ))}
          </SelectInput>
        </Field>
      );
    }
    default: {
      const exhaustive: never = mode;
      return exhaustive;
    }
  }
}
